import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import moment from "moment";
import axios, { AxiosError, AxiosResponse } from "axios";
import { IOpexs } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";

const { Option } = Select;

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (opex?: IOpexs[]) => void;
  opex?: IOpexs;
}

interface IUserFormData {
  trimester: 1 | 2 | 3 | 4;
  realized_opex: number;
  year: number;
  create_date: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const UserModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  opex,
}) => {
  const { t } = useTranslation(["config"]);
  const [loading, setLoading] = useState(false);
  const [lastOPex, setLastOpex] = useState<IOpexs>();
  const [validationErrors, setValidationErrors] = useState({});

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!opex, [opex]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IOpexs[]>("/opexs")
      .then((response) => {
        if (response.data?.length) {
          const arrayOpex = [...response.data];
          arrayOpex.sort((a, b) =>
            moment(b.create_date).diff(moment(a.create_date))
          );
          const last = arrayOpex.filter((objet, idx) => idx === 0)[0];
          setLastOpex(last);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  }, [visible]);

  useEffect(() => {
    form.setFieldsValue({
      ...opex,
      create_at: opex?.create_at && moment(opex.create_at.split("T")[0]),
    });
  }, [form, opex]);

  const handleClose = useCallback(
    (usr?: IOpexs[]) => {
      form.resetFields();
      handleCloseModal(usr);
    },
    [form, handleCloseModal]
  );

  const formatDate = useCallback((date: any) => {
    if (!date) {
      return undefined;
    }
    return date.format("YYYY-MM-DD");
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 401:
          description = "Opex precisa ser adicionado com intervalo de 3 meses";
          break;
        case 404:
          description = "Opex Planejado não foi encontrado";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do equipamento, tente novamente"
            : "Ocorreu um erro ao criar o usuario, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing
          ? t("requestEq.errorCreate")
          : t("requestEq.errorCreate"),
        description,
      });
    },
    [addToast, isEditing, t]
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      try {
        const schema = Yup.object().shape({
          trimester: Yup.number().required("Numero do Trimestre é obrigatório"),
          realized_opex: Yup.number().required("Opex realizado é obrigatório"),
          year: Yup.number().required("Ano é obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { trimester, realized_opex, year, create_date } = data;

        const payload = {
          trimester,
          realized_opex,
          year,
          create_date: create_date ?? null,
        };
        removeNullKeys(payload);
        let isAddOpex = 1;
        if (lastOPex) {
          const currentDate = moment(lastOPex.create_date);
          const lastOpexDate = moment(payload.create_date);
          const diffDias = currentDate.diff(lastOpexDate, "days");
          isAddOpex = Math.abs(diffDias) > 90 ? 1 : 0;
        }

        let response: AxiosResponse;

        if (isEditing) {
          response = await api.patch(`/opexs/${opex?.id}`, payload);
        } else {
          response = await api.post("/opexs", { ...payload, isAddOpex });
        }

        handleClose([response.data]);
      } catch (err) {
        console.log(err);
        addToast({
          type: "error",
          title: ((err as unknown) as any)?.status,
          description: ((err as unknown) as any)?.message,
        });
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      formatDate,
      generateErrorToast,
      handleClose,
      isEditing,
      opex,
      t,
      visible,
      lastOPex,
    ]
  );

  return (
    <Modal
      title={isEditing ? t("modalOpex.edit") : t("modalOpex.create")}
      visible={visible}
      destroyOnClose
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="trimester"
          required
          label={t("opexs.trimester")}
          errors={validationErrors}
        >
          <Select allowClear>
            <Option value="1">{t("1")}</Option>
            <Option value="2">{t("2")}</Option>
            <Option value="3">{t("3")}</Option>
            <Option value="4">{t("4")}</Option>
          </Select>
        </FormItem>
        <FormItem
          name="realized_opex"
          required
          label={t("opexs.realized_opex")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="year"
          required
          label={t("opexs.year")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        {!isEditing && (
          <FormItem
            name="create_date"
            label={t("label")}
            errors={validationErrors}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </FormItem>
        )}
      </Form>
    </Modal>
  );
};

export default UserModal;
